/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.analytics.AnalyticsHelper
import studio.oddpods.cauce.core.base.analytics.OnboardingEvents
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.data.repository.cauce.RecurringScheduleRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.util.BusinessError
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.patient.CreatePatientRequest
import studio.oddpods.cauce.core.model.session.CreateRecurringScheduleRequest
import studio.oddpods.cauce.core.model.session.CreateSessionRequest
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.model.validation.PhoneValidation
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_fee_invalid
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_limit_reached
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_name_required
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_phone_required
import studio.oddpods.cauce.feature.patients.generated.resources.patients_error_save_failed
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_error_fee_invalid
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_error_interval_invalid
import kotlin.time.Clock

sealed interface AddPatientMode {
    data object Regular : AddPatientMode
    data object Onboarding : AddPatientMode
}

enum class SessionKind { ONE_OFF, RECURRING }

data class AddPatientWithSessionState(
    // Patient fields
    val name: String = "",
    val phone: String = "",
    val email: String = "",
    val defaultFeeInput: String = "",
    val notes: String = "",
    // Session section
    val sessionExpanded: Boolean = false,
    val sessionKind: SessionKind = SessionKind.ONE_OFF,
    // One-off fields
    val sessionDate: LocalDate = LocalDate(1970, 1, 1),
    val sessionType: SessionType = SessionType.REGULAR,
    // Recurring fields
    val frequency: RecurrenceFrequency = RecurrenceFrequency.WEEKLY,
    val anchorDate: LocalDate = LocalDate(1970, 1, 1),
    val intervalDaysInput: String = "",
    // Status
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val sessionError: UiText? = null,
)

sealed interface AddPatientWithSessionAction {
    // Patient
    data class NameChanged(val name: String) : AddPatientWithSessionAction
    data class PhoneChanged(val phone: String) : AddPatientWithSessionAction
    data class EmailChanged(val email: String) : AddPatientWithSessionAction
    data class DefaultFeeChanged(val fee: String) : AddPatientWithSessionAction
    data class NotesChanged(val notes: String) : AddPatientWithSessionAction

    // Session section
    data class SessionExpandedChanged(val expanded: Boolean) : AddPatientWithSessionAction
    data class SessionKindChanged(val kind: SessionKind) : AddPatientWithSessionAction

    // One-off
    data class SessionDateChanged(val date: LocalDate) : AddPatientWithSessionAction
    data class SessionTypeChanged(val type: SessionType) : AddPatientWithSessionAction

    // Recurring
    data class FrequencyChanged(val frequency: RecurrenceFrequency) : AddPatientWithSessionAction
    data class AnchorDateChanged(val date: LocalDate) : AddPatientWithSessionAction
    data class IntervalDaysChanged(val days: String) : AddPatientWithSessionAction

    // Submit
    data object SaveClicked : AddPatientWithSessionAction
    data object SkipClicked : AddPatientWithSessionAction
}

sealed interface AddPatientWithSessionEvent {
    data object NavigateBack : AddPatientWithSessionEvent
    data object NavigateToDashboard : AddPatientWithSessionEvent
    data object ShowSessionError : AddPatientWithSessionEvent
}

class AddPatientWithSessionViewModel(
    private val mode: AddPatientMode,
    clock: Clock = Clock.System,
    private val patientRepository: PatientRepository,
    private val sessionRepository: SessionRepository,
    private val recurringScheduleRepository: RecurringScheduleRepository,
    private val profileRepository: ProfileRepository,
    private val analyticsHelper: AnalyticsHelper,
    private val currencyFormatter: CurrencyFormatter,
    val dateFormatter: DateFormatter,
    private val currentTimeMs: () -> Long = { Clock.System.now().toEpochMilliseconds() },
) : BaseViewModel<AddPatientWithSessionState, AddPatientWithSessionEvent, AddPatientWithSessionAction>(
    AddPatientWithSessionState(),
) {

    private val enteredAtMs: Long = currentTimeMs()

    init {
        val today = clock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        mutableStateFlow.value = state.copy(sessionDate = today, anchorDate = today)
        if (mode is AddPatientMode.Onboarding) {
            viewModelScope.launch {
                profileRepository.getCurrentProfile()
                    .onSuccess { profile ->
                        val prefillInput = profile.defaultSessionFee?.let { "${it / 100}" } ?: ""
                        mutableStateFlow.value = state.copy(defaultFeeInput = prefillInput)
                    }
            }
        }
    }

    @Suppress("CyclomaticComplexMethod")
    override fun handleAction(action: AddPatientWithSessionAction) {
        when (action) {
            is AddPatientWithSessionAction.NameChanged ->
                mutableStateFlow.value = state.copy(name = action.name, error = null)

            is AddPatientWithSessionAction.PhoneChanged ->
                mutableStateFlow.value = state.copy(phone = action.phone, error = null)

            is AddPatientWithSessionAction.EmailChanged ->
                mutableStateFlow.value = state.copy(email = action.email, error = null)

            is AddPatientWithSessionAction.DefaultFeeChanged ->
                mutableStateFlow.value = state.copy(defaultFeeInput = action.fee, error = null)

            is AddPatientWithSessionAction.NotesChanged ->
                mutableStateFlow.value = state.copy(notes = action.notes, error = null)

            is AddPatientWithSessionAction.SessionExpandedChanged ->
                mutableStateFlow.value = state.copy(
                    sessionExpanded = action.expanded,
                    sessionError = null,
                )

            is AddPatientWithSessionAction.SessionKindChanged -> handleSessionKindChanged(action.kind)
            is AddPatientWithSessionAction.SessionDateChanged ->
                mutableStateFlow.value = state.copy(sessionDate = action.date, sessionError = null)

            is AddPatientWithSessionAction.SessionTypeChanged ->
                mutableStateFlow.value = state.copy(sessionType = action.type, sessionError = null)

            is AddPatientWithSessionAction.FrequencyChanged ->
                mutableStateFlow.value = state.copy(
                    frequency = action.frequency,
                    sessionError = null,
                )

            is AddPatientWithSessionAction.AnchorDateChanged ->
                mutableStateFlow.value = state.copy(anchorDate = action.date, sessionError = null)

            is AddPatientWithSessionAction.IntervalDaysChanged ->
                mutableStateFlow.value =
                    state.copy(intervalDaysInput = action.days, sessionError = null)

            AddPatientWithSessionAction.SaveClicked -> handleSave()
            AddPatientWithSessionAction.SkipClicked -> handleSkip()
        }
    }

    private fun handleSessionKindChanged(kind: SessionKind) {
        val current = state
        val newState = when (kind) {
            SessionKind.ONE_OFF -> {
                current.copy(sessionKind = kind, sessionError = null)
            }

            SessionKind.RECURRING -> {
                current.copy(sessionKind = kind, sessionError = null)
            }
        }
        mutableStateFlow.value = newState
    }

    @Suppress("ReturnCount", "CyclomaticComplexMethod")
    private fun handleSave() {
        val name = state.name.trim()
        val phone = state.phone.trim()
        val feeCentavos = currencyFormatter.parse(state.defaultFeeInput)

        if (name.isBlank()) {
            mutableStateFlow.value =
                state.copy(error = UiText.Resource(Res.string.patients_error_name_required))
            return
        }
        if (phone.isBlank()) {
            mutableStateFlow.value =
                state.copy(error = UiText.Resource(Res.string.patients_error_phone_required))
            return
        }
        if (feeCentavos == null || feeCentavos <= 0) {
            mutableStateFlow.value =
                state.copy(error = UiText.Resource(Res.string.patients_error_fee_invalid))
            return
        }

        if (state.sessionExpanded) {
            val sessionValidationError = validateSession()
            if (sessionValidationError != null) {
                mutableStateFlow.value = state.copy(sessionError = sessionValidationError)
                return
            }
        }

        val normalizedPhone = PhoneValidation.normalizePhone(phone)

        mutableStateFlow.value = state.copy(isSaving = true, error = null, sessionError = null)
        viewModelScope.launch {
            patientRepository.createPatient(
                CreatePatientRequest(
                    name = name,
                    phone = normalizedPhone,
                    email = state.email.trim().ifBlank { null },
                    defaultFee = feeCentavos,
                    notes = state.notes.trim().ifBlank { null },
                ),
            ).onSuccess { patient ->
                var sessionCreated = false
                if (state.sessionExpanded) {
                    sessionCreated = createSession(patient.id)
                }
                postSave(sessionCreated)
            }.onFailure { e ->
                val error = when (e) {
                    is BusinessError.LimitReached -> UiText.Resource(Res.string.patients_error_limit_reached)
                    else -> e.toUiText(Res.string.patients_error_save_failed)
                }
                mutableStateFlow.value = state.copy(isSaving = false, error = error)
            }
        }
    }

    private fun validateSession(): UiText? = when (state.sessionKind) {
        SessionKind.ONE_OFF -> {
            val fee = currencyFormatter.parse(state.defaultFeeInput)
            if (fee == null || fee <= 0) {
                UiText.Resource(Res.string.patients_session_error_fee_invalid)
            } else {
                null
            }
        }

        SessionKind.RECURRING -> {
            val fee = currencyFormatter.parse(state.defaultFeeInput)
            val invalidInterval = state.frequency == RecurrenceFrequency.CUSTOM &&
                (state.intervalDaysInput.toIntOrNull()?.let { it <= 0 } ?: true)
            when {
                fee == null || fee <= 0 ->
                    UiText.Resource(Res.string.patients_session_error_fee_invalid)

                invalidInterval ->
                    UiText.Resource(Res.string.patients_session_error_interval_invalid)

                else -> null
            }
        }
    }

    private suspend fun createSession(patientId: String): Boolean {
        return when (state.sessionKind) {
            SessionKind.ONE_OFF -> {
                val fee = currencyFormatter.parse(state.defaultFeeInput)!!
                sessionRepository.createSession(
                    CreateSessionRequest(
                        patientId = patientId,
                        sessionDate = state.sessionDate.toString(),
                        fee = fee,
                        sessionType = state.sessionType.name,
                    ),
                ).isSuccess
            }

            SessionKind.RECURRING -> {
                val fee = currencyFormatter.parse(state.defaultFeeInput)!!
                val intervalDays = if (state.frequency == RecurrenceFrequency.CUSTOM) {
                    state.intervalDaysInput.toIntOrNull()
                } else {
                    null
                }
                recurringScheduleRepository.createSchedule(
                    CreateRecurringScheduleRequest(
                        patientId = patientId,
                        frequency = state.frequency,
                        intervalDays = intervalDays,
                        dayOfWeek = state.anchorDate.dayOfWeek.ordinal + 1,
                        fee = fee,
                        anchorDate = state.anchorDate.toString(),
                    ),
                ).isSuccess
            }
        }
    }

    private suspend fun postSave(sessionCreated: Boolean) {
        mutableStateFlow.value = state.copy(isSaving = false)
        if (state.sessionExpanded && !sessionCreated) {
            sendEvent(AddPatientWithSessionEvent.ShowSessionError)
        }
        when (mode) {
            AddPatientMode.Onboarding -> {
                val screenTimeSeconds =
                    ((currentTimeMs() - enteredAtMs) / 1000L).toInt().coerceAtLeast(0)
                val hasEmail = state.email.trim().isNotBlank()
                analyticsHelper.logEvent(
                    OnboardingEvents.firstPatientAdded(
                        hasEmail = hasEmail,
                        feeModifiedFromDefault = true,
                        screenTimeSeconds = screenTimeSeconds,
                    ),
                )
                if (sessionCreated) {
                    analyticsHelper.logEvent(
                        OnboardingEvents.firstSessionLogged(
                            paid = false,
                            screenTimeSeconds = screenTimeSeconds,
                        ),
                    )
                }
                profileRepository.completeOnboarding()
                sendEvent(AddPatientWithSessionEvent.NavigateToDashboard)
            }

            AddPatientMode.Regular -> sendEvent(AddPatientWithSessionEvent.NavigateBack)
        }
    }

    private fun handleSkip() {
        if (mode !is AddPatientMode.Onboarding) return
        analyticsHelper.logEvent(OnboardingEvents.firstPatientSkipped())
        viewModelScope.launch {
            profileRepository.completeOnboarding()
            sendEvent(AddPatientWithSessionEvent.NavigateToDashboard)
        }
    }
}
